// @ts-nocheck
import { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  Grid,
  Box,
  IconButton,
  Typography,
  styled,
  Stack,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import { ReactComponent as CloseIcon } from '../../../../../assets/Icons/lucideIcons/x.svg';
import { ReactComponent as CircleIcon } from '../../../../../assets/Icons/lucideIcons/circle.svg';
import { ReactComponent as ExpandMoreIcon } from '../../../../../assets/Icons/lucideIcons/chevron-down.svg';
import DialogTransition from '../../../../../components/Common/Transition/DialogTransition';

const getStatusColor = (status, theme) => {
  switch (status) {
    case 'Success':
      return theme?.palette?.success?.light;
    case 'Failed':
      return theme?.palette?.error?.main;
    default:
      return theme?.palette?.text;
  }
};

const StatusCircleIcon = styled(CircleIcon, {
  shouldForwardProp: (prop) => prop !== 'status',
})(({ theme, status }) => ({
  color: getStatusColor(status, theme),
}));

const AutomationResultModal = ({ open, handleClose, taskResult }) => {
  const [expandSection, setExpandSection] = useState('');

  const automationResult = useMemo(() => {
    const automationResultData = taskResult?.result;
    return automationResultData || {};
  }, [taskResult]);

  const handleExpansionSection = useCallback(
    (automationName) => () => {
      setExpandSection((prev) =>
        prev === automationName ? null : automationName
      );
    },
    [setExpandSection]
  );
  return (
    <Box>
      <Dialog
        disableBackdropClick
        onClose={handleClose}
        open={open}
        maxWidth="lg"
        fullWidth={true}
        aria-labelledby="automation-result-model"
        slots={{
          transition: DialogTransition,
        }}
      >
        <DialogTitle id="automation-result-model">
          Automation Tasks Report
        </DialogTitle>
        <IconButton
          color="primary"
          aria-label="Close dialog"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 16,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent dividers>
          <Grid
            container
            sx={{
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Grid
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
              size={9}
            >
              {Object.keys(automationResult || {})?.map((automationName) => {
                const actions = automationResult[automationName];
                return (
                  <Box key={automationName}>
                    <Accordion
                      expanded={expandSection === automationName}
                      aria-label={automationName}
                      onChange={handleExpansionSection(automationName)}
                      sx={(theme) => ({
                        border: `1px solid ${theme?.palette?.other?.borderColor}`,
                      })}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>{automationName}</Typography>
                      </AccordionSummary>
                      <AccordionDetails
                        sx={{
                          p: '12px',
                        }}
                      >
                        {Object.keys(actions).map((actionName) => {
                          // currently status and msg are coming, we are using just status
                          const { status } = actions[actionName];
                          return (
                            <Stack
                              key={`${automationName}_${actionName}`}
                              sx={{
                                flexDirection: 'row',
                                py: '5px',
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 2,
                                }}
                              >
                                <Tooltip
                                  title={status}
                                  placement="top-start"
                                  leaveDelay={100}
                                >
                                  <StatusCircleIcon
                                    width={18}
                                    height={18}
                                    status={status}
                                  />
                                </Tooltip>
                                <Typography
                                  title={actionName}
                                  variant="subtitle2"
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    color: 'text.primary',
                                    whiteSpace: 'nowrap',
                                    fontWeight: 500,
                                  }}
                                >
                                  {actionName}
                                </Typography>
                              </Box>
                            </Stack>
                          );
                        })}
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                );
              })}
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

AutomationResultModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  taskResult: PropTypes.oneOfType([PropTypes.object]).isRequired,
};

export default AutomationResultModal;
